/**
 * Scroll the viewport to a form's top-level anchor after it is mounted.
 *
 * Avoids relying solely on native hash scrolling (which can fire before a
 * lazy route mounts) and clears stale focus that would pull the browser back
 * to a previously focused field mid-form.
 */

const DEFAULT_RETRIES = 24;
const RETRY_MS = 50;

function clearStaleFocus(target) {
  const active = document.activeElement;
  if (!active || active === document.body || active === document.documentElement) {
    return;
  }
  // If focus is already on the destination (or inside it after a deliberate
  // preventScroll focus), leave it alone.
  if (target && (active === target || target.contains(active))) {
    return;
  }
  if (typeof active.blur === "function") {
    active.blur();
  }
}

/**
 * @param {string} anchorId - element id of the form top container
 * @param {{ behavior?: ScrollBehavior, retries?: number, updateHash?: boolean, pathname?: string }} [options]
 * @returns {() => void} cancel function
 */
export function scrollToFormTop(anchorId, options = {}) {
  if (!anchorId || typeof document === "undefined") {
    return () => {};
  }

  const {
    behavior = "smooth",
    retries = DEFAULT_RETRIES,
    updateHash = true,
    pathname,
  } = options;

  let cancelled = false;
  let attempt = 0;
  let timerId = 0;

  const run = () => {
    if (cancelled) return;
    const el = document.getElementById(anchorId);
    if (!el) {
      attempt += 1;
      if (attempt <= retries) {
        timerId = window.setTimeout(run, RETRY_MS);
      }
      return;
    }

    clearStaleFocus(el);

    // Instant jump first clears any restored mid-page scroll, then optional
    // smooth alignment is unnecessary once we are already at the top.
    el.scrollIntoView({ behavior, block: "start", inline: "nearest" });

    if (updateHash) {
      const nextHash = `#${anchorId}`;
      const nextPath = pathname || window.location.pathname;
      if (window.location.hash !== nextHash || window.location.pathname !== nextPath) {
        window.history.replaceState(null, "", `${nextPath}${nextHash}`);
      }
    }

    // Accessible focus without a second scroll jump.
    if (!el.hasAttribute("tabindex")) {
      el.setAttribute("tabindex", "-1");
    }
    try {
      el.focus({ preventScroll: true });
    } catch {
      el.focus();
    }
  };

  // Double rAF: wait for layout after Suspense / conditional mount.
  timerId = window.requestAnimationFrame(() => {
    window.requestAnimationFrame(run);
  });

  return () => {
    cancelled = true;
    if (timerId) {
      window.clearTimeout(timerId);
      window.cancelAnimationFrame(timerId);
    }
  };
}
